import { attachPopoverEvents } from './popover.utils';
import { attachTabEvents } from './tab.utils';

type Constructor<T = {}> = new (...args: any[]) => T;

export const bs5Connect = <T extends Constructor<HTMLElement>>(Base: T) => {
  return class extends Base {
    /**
     * Initializes all matching elements with the provided Bootstrap component.
     *
     * @param query - CSS selector for target elements.
     * @param bsComponent - Bootstrap component class (e.g., window.bootstrap?.Popover).
     * @param config - Optional configuration for the Bootstrap component.
     * @returns An array of objects with `firstInit` (whether it was newly initialized) and `instance` (the Bootstrap component instance).
     */
    bsInitAll<U>(query: string, bsComponent: any, config?: any): { firstInit: boolean; instance: U }[] {
      const shadowRoot = this.shadowRoot;
      const instancesList: { firstInit: boolean; instance: U }[] = [];

      if (!bsComponent) {
        console.error('bsComponent parameter provided to bsInit is not truthy:', bsComponent);
        return instancesList;
      }

      if (typeof bsComponent.getInstance !== 'function') {
        console.error(
          'bsComponent does not have a getInstance method. It may not be a Bootstrap component class:',
          bsComponent
        );
        return instancesList;
      }

      if (typeof bsComponent !== 'function') {
        console.error('bsComponent is not a constructor. It may not be a Bootstrap component class:', bsComponent);
        return instancesList;
      }

      if (shadowRoot) {
        const container = shadowRoot.querySelector('.uxt-mhcorp');
        if (container) {
          shadowRoot.querySelectorAll(query).forEach((element) => {
            const instance = bsComponent.getInstance(element);
            if (instance) {
              instancesList.push({ firstInit: false, instance });
            } else {
              const newInstance = new bsComponent(element, { container, ...config });
              this.attachComponentEvents(element, bsComponent, newInstance);
              instancesList.push({ firstInit: true, instance: newInstance });
            }
          });
        }
      }

      return instancesList;
    }

    /**
     * Initializes the first matching element and returns its Bootstrap instance.
     *
     * @param query - CSS selector for the target element.
     * @param bsComponent - Bootstrap component class.
     * @param config - Optional configuration for the Bootstrap component.
     * @returns The instance of the Bootstrap component for the first matched element, or `undefined` if none found.
     */
    bsInit(query: string, bsComponent: any, config?: any): { firstInit: boolean; instance: any } | undefined {
      return this.bsInitAll(query, bsComponent, config)[0];
    }

    /**
     * Attaches appropriate event listeners for Bootstrap components.
     *
     * @param element - The target element.
     * @param bsComponent - The Bootstrap component class.
     * @param instance - The initialized Bootstrap component instance.
     */
    attachComponentEvents(element: Element, bsComponent: any, instance: any): void {
      if (bsComponent === window.bootstrap?.Tab) {
        attachTabEvents(element, instance, this.shadowRoot);
      } else if (bsComponent === window.bootstrap?.Popover) {
        attachPopoverEvents(element, instance, this);
      }
    }
  };
};
